import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { leadFormConfigEndpoint } from "@/config/endpoints";
import {
  selectIsBoardViewHideExempt,
  selectIsLeadPhoneMaskExempt,
} from "@/rbac/rbacSlice";

/**
 * Tenant-wide "Add Lead" REQUIRED-FIELDS policy. The list holds field KEYS
 * ("basic:email", "source:branch", "pref:<id>", …) that every user must fill
 * when creating a lead. GET is for everyone (so the form enforces it for all);
 * PUT is Super-Admin-only (the server rejects non-admins).
 */
const KEY = ["lead-form-config", "required-fields"];

export function useLeadFormRequiredFields() {
  const query = useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<string[]> => {
      const res = await axiosInstance.get(leadFormConfigEndpoint);
      return (res.data?.requiredFields ?? []) as string[];
    },
    staleTime: 5 * 60_000,
  });
  return { requiredFields: query.data ?? [], isLoading: query.isLoading };
}

/**
 * Tenant-wide LOCKED-FIELDS policy. A locked field can still be set when a
 * lead is created — it just can't be changed afterwards, by anyone (there is
 * no admin bypass; the server enforces it independently of this hook).
 *
 * `lockableFields` is the catalog the server ships alongside the config, so
 * the settings UI can only ever offer toggles the server actually enforces.
 */
export type LockableField = { key: string; label: string };

const LOCK_KEY = ["lead-form-config", "locked-fields"];

/** Settings → Manage "Dashboard default role" values (server enum). */
export type FollowUpDefaultRoleMode = "auto" | "current_role";

/**
 * Settings → Manage "Default view" — which side of the leads list's
 * Active / Inactive toggle every list OPENS on. Wire values are the toggle's
 * own values, so nothing has to be translated on the way in.
 */
export type DefaultLeadView = "all" | "Active" | "Inactive";

/**
 * Settings → Manage "Sidebar opens on" — which of the sidebar's paired
 * sections (STAGES or FAVORITE VIEWS, one open at a time) is expanded by
 * default.
 */
export type SidebarDefaultOpen = "favorites" | "stages";

const EMPTY_CONVERSION_GOALS: ConversionGoal[] = [];

/**
 * "Conversion goal": what counts as a conversion on one pipeline. `pipelineId`
 * null = the default pipeline; a pipeline with no entry converts on its last
 * stage.
 */
export interface ConversionGoal {
  pipelineId: number | null;
  stageId: number;
  statusId: number | null;
  subStatusId: number | null;
}

/** Settings → Manage "Check-in / Check-out" (server: LeadFormConfigService.getCheckInOutSettings). */
export interface CheckInOutSettings {
  enabled: boolean;
  /** Minutes of inactivity before an automatic check-out; null = never. */
  inactivityMinutes: number | null;
  /** Daily wall-clock auto check-out, IST "HH:mm"; null = none. */
  autoCheckoutAt: string | null;
}
const EMPTY_CHECK_IN_OUT: CheckInOutSettings = {
  enabled: false,
  inactivityMinutes: null,
  autoCheckoutAt: null,
};
function parseCheckInOut(raw: any): CheckInOutSettings {
  // Defaults OFF (mirrors the server's absent-row default): an older server
  // or a failed read must not start prompting people to check in.
  const minutes = Number(raw?.inactivityMinutes);
  return {
    enabled: raw?.enabled === true,
    inactivityMinutes:
      raw?.inactivityMinutes != null && Number.isFinite(minutes) && minutes > 0
        ? minutes
        : null,
    autoCheckoutAt:
      typeof raw?.autoCheckoutAt === "string" && raw.autoCheckoutAt
        ? raw.autoCheckoutAt
        : null,
  };
}

/** Stable "every tab shown" value so consumers' memo deps don't churn. */
const EMPTY_TAB_NAMES: string[] = [];
function parseTabNames(raw: unknown): string[] {
  if (!Array.isArray(raw)) return EMPTY_TAB_NAMES;
  return raw.filter((t): t is string => typeof t === "string" && t !== "");
}

/** Stable "every Add Lead field shown" value so memo deps don't churn. */
const EMPTY_FIELD_KEYS: string[] = [];

/** Stable "every role" value so consumers' memo deps don't churn. */
const EMPTY_ROLE_IDS: number[] = [];
function parseRoleIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return EMPTY_ROLE_IDS;
  return raw.map(Number).filter((n) => Number.isInteger(n) && n > 0);
}

export function useLeadFormLockedFields() {
  const query = useQuery({
    queryKey: LOCK_KEY,
    queryFn: async (): Promise<{
      lockedFields: string[];
      lockableFields: LockableField[];
      showPreviousAssignees: boolean;
      maskLeadPhone: boolean;
      leadBasicDetailsExpanded: boolean;
      leadViewDisplay: Record<string, any>;
      showMoveToNextStage: boolean;
      followUpTimeEnabled: boolean;
      /** Minutes before a timed follow-up that its due popup rings (0 = at the time). */
      followUpRemindMinutesBefore: number;
      showLeadId: boolean;
      /** STORED: null = automatic, 0 = explicitly "All roles", >0 = role id. */
      defaultFollowUpRoleId: number | null;
      /** RESOLVED: the role the Follow-ups widget opens on (null = all). */
      resolvedDefaultFollowUpRoleId: number | null;
      globalLeadSearchEnabled: boolean;
      showOnlyNextStage: boolean;
      /** Tenant-wide Add Lead field order (empty = canonical). */
      addLeadFieldOrder: string[];
      /** Tenant-wide Add Lead fields hidden from the form ([] = all shown). */
      addLeadHiddenFields: string[];
      allowCreatedAtEdit: boolean;
      /** Org-wide: the Board tab is hidden on every lead list. */
      boardViewDisabled: boolean;
      /** Org-wide: deals are OFF — no deal surface anywhere, no deal can be created. */
      dealsDisabled: boolean;
      /** Org-wide: lead-drawer tabs hidden for everyone ([] = all shown). */
      hiddenLeadViewTabs: string[];
      /** Org-wide: the dashboard's Branch dropdowns are hidden. */
      dashboardBranchFilterHidden: boolean;
      /** Org-wide: an admin's saved layouts are everyone's layouts. */
      viewConfigGlobalised: boolean;
      /** Which role the dashboard follow-up pills open on: "auto" (first
       *  personal role under the viewer) or "current_role" (the viewer's own). */
      followUpDefaultRoleMode: FollowUpDefaultRoleMode;
      /** "Give it as a fresh lead" landing sub-status (null = the first one). */
      freshLeadSubStatusId: number | null;
      /** "Pop assigned users": a chat attachment pops the LEAD's assignees. */
      chatAttachmentPopupLeadUsers: boolean;
      /** "Pop assigned users": a chat attachment pops the DEAL's assignees. */
      chatAttachmentPopupDealUsers: boolean;
      /** "Pop assigned users": a note added on a lead / deal pops its audience. */
      notePopupAssignedUsers: boolean;
      /** "Pop assigned users": only these roles get those popups ([] = every role). */
      popupRoleIds: number[];
      /** "Chat & notes sync": notes post into the chat, chat saves as notes. */
      chatNotesSync: boolean;
      /** "Check-in / Check-out": on/off + the two auto check-out options. */
      checkInOut: CheckInOutSettings;
      /** "Default view": where the Active / Inactive toggle starts. */
      defaultLeadView: DefaultLeadView;
      /** "Count only CRM numbers": the call numbers count only calls filed
       *  under a lead (true) or every call (false, the default). */
      callAnalyticsCrmOnly: boolean;
      /** "WhatsApp number counts as the lead's number": the duplicate check
       *  treats a lead's WhatsApp number as one of its numbers (true) or only
       *  its phone (false, the default). */
      whatsappNumberUnique: boolean;
      /** "Denied stages stay hidden everywhere": a lead / deal in a stage a
       *  role denies is never shown to that role (true, the default) or the
       *  older visibility rules apply (false). */
      stageDenyStrict: boolean;
      /** "Payment analytics": the RESOLVED switch behind every money surface
       *  (stored value, else the industry default). null = the server did not
       *  send it (an older server) — `usePaymentAnalyticsEnabled` then falls
       *  back to the rule that existed before the setting. */
      paymentAnalytics: boolean | null;
      /** "Conversion goal": the pipelines with a picked goal. Empty = every
       *  pipeline converts on its last stage. */
      conversionGoals: ConversionGoal[];
      /** "Sidebar opens on": STAGES or FAVORITE VIEWS expanded by default. */
      sidebarDefaultOpen: SidebarDefaultOpen;
    }> => {
      const res = await axiosInstance.get(leadFormConfigEndpoint);
      return {
        lockedFields: (res.data?.lockedFields ?? []) as string[],
        lockableFields: (res.data?.lockableFields ?? []) as LockableField[],
        showPreviousAssignees: Boolean(res.data?.showPreviousAssignees),
        maskLeadPhone: Boolean(res.data?.maskLeadPhone),
        leadBasicDetailsExpanded: Boolean(res.data?.leadBasicDetailsExpanded),
        leadViewDisplay: (res.data?.leadViewDisplay ?? {}) as Record<
          string,
          any
        >,
        // Defaults ON: an older server (or a failed read) must not hide the
        // drawer's primary action. Only an explicit `false` turns it off.
        showMoveToNextStage: res.data?.showMoveToNextStage !== false,
        // Same defaults-ON reasoning: capturing a time is the historical
        // behaviour, so only an explicit `false` makes follow-ups date-only.
        followUpTimeEnabled: res.data?.followUpTimeEnabled !== false,
        // An older server sends nothing: 0 = ring at the time, as before.
        followUpRemindMinutesBefore: Number(res.data?.followUpRemindMinutesBefore) || 0,
        // Defaults OFF: only an explicit `true` prints the ID.
        showLeadId: Boolean(res.data?.showLeadId),
        // `?? null` is NOT enough here — 0 is a MEANING ("explicitly all
        // roles"), not an absent value, so it must survive the parse.
        defaultFollowUpRoleId:
          res.data?.defaultFollowUpRoleId == null
            ? null
            : Number(res.data.defaultFollowUpRoleId),
        resolvedDefaultFollowUpRoleId:
          res.data?.resolvedDefaultFollowUpRoleId == null
            ? null
            : Number(res.data.resolvedDefaultFollowUpRoleId),
        // Defaults OFF, and only an explicit `true` opens it: this one lifts a
        // read-scope wall, so an older server or a failed read must land on
        // "unavailable", never on "available".
        globalLeadSearchEnabled: res.data?.globalLeadSearchEnabled === true,
        // Defaults OFF: only an explicit `true` narrows the picker, so an
        // older server leaves stage moves exactly as they are today.
        showOnlyNextStage: res.data?.showOnlyNextStage === true,
        addLeadFieldOrder: Array.isArray(res.data?.addLeadFieldOrder)
          ? (res.data.addLeadFieldOrder as unknown[]).filter(
              (k): k is string => typeof k === "string",
            )
          : [],
        // Absent / garbled = nothing hidden, so an older server shows the
        // whole form.
        addLeadHiddenFields: Array.isArray(res.data?.addLeadHiddenFields)
          ? (res.data.addLeadHiddenFields as unknown[]).filter(
              (k): k is string => typeof k === "string",
            )
          : EMPTY_FIELD_KEYS,
        // Defaults OFF, and only an explicit `true` opens it: this lifts a
        // write wall (back-dating a lead), and the server rejects a
        // `createdAt` while it's off, so an older server must never render
        // the field.
        allowCreatedAtEdit: res.data?.allowCreatedAtEdit === true,
        // Defaults OFF (board shown): only an explicit `true` removes the
        // tab, so an older server or a failed read never hides a view.
        boardViewDisabled: res.data?.boardViewDisabled === true,
        // Defaults OFF (deals on): only an explicit `true` hides the deal
        // concept, so an older server never strips a workspace's deals.
        dealsDisabled: res.data?.dealsDisabled === true,
        // [] = every tab shown — also what an older server (which never sends
        // it) means, so a lagging deploy never hides a drawer tab.
        hiddenLeadViewTabs: parseTabNames(res.data?.hiddenLeadViewTabs),
        // Defaults OFF (filters shown): only an explicit `true` removes them,
        // so an older server or a failed read never hides a control.
        dashboardBranchFilterHidden:
          res.data?.dashboardBranchFilterHidden === true,
        // Defaults OFF: only an explicit `true` locks layouts, so an older
        // server never takes a user's own arrangement away.
        viewConfigGlobalised: res.data?.viewConfigGlobalised === true,
        // Defaults "auto" (today's rule): only the explicit value switches
        // the pills to the viewer's own role.
        followUpDefaultRoleMode:
          res.data?.followUpDefaultRoleMode === "current_role"
            ? "current_role"
            : "auto",
        // Defaults null (first sub-status): an older server simply never
        // sends it, which is exactly the historical landing.
        freshLeadSubStatusId:
          Number(res.data?.freshLeadSubStatusId) > 0
            ? Number(res.data.freshLeadSubStatusId)
            : null,
        // Defaults ON (mirrors the server's absent-row default): only an
        // explicit `false` stops the popup, so an older server or a failed
        // read keeps telling assignees that a file landed.
        chatAttachmentPopupLeadUsers:
          res.data?.chatAttachmentPopupLeadUsers !== false,
        chatAttachmentPopupDealUsers:
          res.data?.chatAttachmentPopupDealUsers !== false,
        // Same defaults-ON shape: only an explicit `false` mutes note popups.
        notePopupAssignedUsers: res.data?.notePopupAssignedUsers !== false,
        // [] = every role (also what an older server, which never sends it,
        // means).
        popupRoleIds: parseRoleIds(res.data?.popupRoleIds),
        // Defaults ON (mirrors the server's absent-row default): only an
        // explicit `false` turns the sync off.
        chatNotesSync: res.data?.chatNotesSync !== false,
        checkInOut: parseCheckInOut(res.data?.checkInOut),
        // Defaults "all" (today's behaviour) — only the two explicit values
        // seed the toggle, so an older server or a garbled read can never
        // narrow someone's list.
        defaultLeadView:
          res.data?.defaultLeadView === "Active"
            ? "Active"
            : res.data?.defaultLeadView === "Inactive"
              ? "Inactive"
              : "all",
        // Defaults OFF (every call counted): only an explicit `true` reads as
        // CRM-only. The server enforces the mode itself; this flag only
        // drives the settings card and the captions.
        callAnalyticsCrmOnly: res.data?.callAnalyticsCrmOnly === true,
        // Defaults OFF (phone-only duplicate rule): only an explicit `true`
        // reads as ON. The server enforces the rule itself.
        whatsappNumberUnique: res.data?.whatsappNumberUnique === true,
        // Defaults ON: only an explicit `false` reads as off. The server
        // enforces it; this flag only drives the settings card.
        stageDenyStrict: res.data?.stageDenyStrict !== false,
        // Only a real boolean counts — anything else means "this server
        // doesn't know the setting", never "off".
        paymentAnalytics:
          typeof res.data?.paymentAnalytics === "boolean"
            ? res.data.paymentAnalytics
            : null,
        conversionGoals: Array.isArray(res.data?.conversionGoals)
          ? res.data.conversionGoals
          : [],
        // Defaults "favorites" (today's sidebar): only the explicit value
        // opens STAGES, so an older server keeps the sidebar as it was.
        sidebarDefaultOpen:
          res.data?.sidebarDefaultOpen === "stages" ? "stages" : "favorites",
      };
    },
    staleTime: 5 * 60_000,
  });
  const lockedFields = query.data?.lockedFields ?? [];
  return {
    lockedFields,
    lockableFields: query.data?.lockableFields ?? [],
    showPreviousAssignees: query.data?.showPreviousAssignees ?? false,
    maskLeadPhone: query.data?.maskLeadPhone ?? false,
    leadBasicDetailsExpanded: query.data?.leadBasicDetailsExpanded ?? false,
    leadViewDisplay: query.data?.leadViewDisplay ?? {},
    showMoveToNextStage: query.data?.showMoveToNextStage ?? true,
    followUpTimeEnabled: query.data?.followUpTimeEnabled ?? true,
    followUpRemindMinutesBefore: query.data?.followUpRemindMinutesBefore ?? 0,
    showLeadId: query.data?.showLeadId ?? false,
    freshLeadSubStatusId: query.data?.freshLeadSubStatusId ?? null,
    defaultFollowUpRoleId: query.data?.defaultFollowUpRoleId ?? null,
    resolvedDefaultFollowUpRoleId:
      query.data?.resolvedDefaultFollowUpRoleId ?? null,
    globalLeadSearchEnabled: query.data?.globalLeadSearchEnabled ?? false,
    showOnlyNextStage: query.data?.showOnlyNextStage ?? false,
    addLeadFieldOrder: query.data?.addLeadFieldOrder ?? [],
    addLeadHiddenFields: query.data?.addLeadHiddenFields ?? EMPTY_FIELD_KEYS,
    allowCreatedAtEdit: query.data?.allowCreatedAtEdit ?? false,
    boardViewDisabled: query.data?.boardViewDisabled ?? false,
    dealsDisabled: query.data?.dealsDisabled ?? false,
    hiddenLeadViewTabs: query.data?.hiddenLeadViewTabs ?? EMPTY_TAB_NAMES,
    dashboardBranchFilterHidden:
      query.data?.dashboardBranchFilterHidden ?? false,
    viewConfigGlobalised: query.data?.viewConfigGlobalised ?? false,
    followUpDefaultRoleMode: query.data?.followUpDefaultRoleMode ?? "auto",
    chatAttachmentPopupLeadUsers:
      query.data?.chatAttachmentPopupLeadUsers ?? true,
    chatAttachmentPopupDealUsers:
      query.data?.chatAttachmentPopupDealUsers ?? true,
    notePopupAssignedUsers: query.data?.notePopupAssignedUsers ?? true,
    popupRoleIds: query.data?.popupRoleIds ?? EMPTY_ROLE_IDS,
    chatNotesSync: query.data?.chatNotesSync ?? true,
    checkInOut: query.data?.checkInOut ?? EMPTY_CHECK_IN_OUT,
    defaultLeadView: query.data?.defaultLeadView ?? "all",
    callAnalyticsCrmOnly: query.data?.callAnalyticsCrmOnly ?? false,
    whatsappNumberUnique: query.data?.whatsappNumberUnique ?? false,
    stageDenyStrict: query.data?.stageDenyStrict ?? true,
    paymentAnalytics: query.data?.paymentAnalytics ?? null,
    conversionGoals: query.data?.conversionGoals ?? EMPTY_CONVERSION_GOALS,
    sidebarDefaultOpen: query.data?.sidebarDefaultOpen ?? "favorites",
    isLoading: query.isLoading,
    /** True when this field key may not be edited on an existing lead. */
    isFieldLocked: (key: string) => lockedFields.includes(key),
  };
}

/**
 * Whether THIS viewer should see phone numbers masked on the list/grid/board.
 *
 * Two conditions, both required:
 *   1. the org-wide `maskLeadPhone` flag is on, and
 *   2. the viewer does NOT hold an admin-preset role.
 *
 * Only super admins and ADMIN-preset roles are exempt — they run the business
 * off these screens (verifying, de-duping, reading a number out loud) and
 * they're not the leak risk the setting exists to deter. Every other preset
 * (manager, editor, counsellor, viewer, guest) is masked, including whoever
 * sits at the top of the role hierarchy.
 *
 * This used to reuse `selectCanBypassRbacChecks`, which ALSO exempts
 * `roleHierarchy === 1` and PLATFORM_STAFF — so the tenant's top non-admin role
 * kept seeing full numbers and the feature read as broken. See
 * `selectIsLeadPhoneMaskExempt`.
 *
 * Baked into this one hook rather than left to each call site, so a new caller
 * can't forget the exemption and mask an admin's screen.
 *
 * Defaults to `false` while the config query is still loading — numbers appear
 * unmasked for that first tick rather than flashing masked-then-revealed.
 */
export function useMaskLeadPhone(): boolean {
  const { maskLeadPhone } = useLeadFormLockedFields();
  const isExempt = useSelector(selectIsLeadPhoneMaskExempt);
  return maskLeadPhone && !isExempt;
}

export function useSetMaskLeadPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (maskLeadPhone: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/mask-lead-phone`,
        { maskLeadPhone },
      );
      return Boolean(res.data?.maskLeadPhone);
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        maskLeadPhone: saved,
      }));
      toast.success(
        saved
          ? "Phone numbers are now hidden on the leads list"
          : "Phone numbers are now fully visible",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Org-wide "the lead drawer's Basic details card opens expanded" flag.
 *
 * TENANT-WIDE on purpose, not a per-browser preference: the tenant decides how
 * the drawer opens for its whole team, so a counsellor on a new machine sees
 * the same layout as everyone else. Reads off the same cached config query as
 * the locked-fields hook, so the drawer costs no extra request.
 *
 * Defaults to `false` (compact) while the query is loading — that matches the
 * historical behaviour, so the card can only ever animate open, never shut.
 */
/**
 * Org-wide "View Lead Display" map — which drawer sections/fields are visible.
 * Reads off the same cached config GET as the other org flags (no extra
 * request). ABSENT KEY = VISIBLE, so `{}` means "hide nothing".
 *
 * This lives in the DB rather than redux-persist because it is an ORG policy:
 * browser-local storage made it per-device and wiped it on every logout.
 */
export function useLeadViewDisplay(): Record<string, any> {
  const { leadViewDisplay } = useLeadFormLockedFields();
  return leadViewDisplay;
}

export function useSetLeadViewDisplay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      leadViewDisplay: Record<string, any>,
    ): Promise<Record<string, any>> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/view-display`,
        { leadViewDisplay },
      );
      return (res.data?.leadViewDisplay ?? {}) as Record<string, any>;
    },
    // Optimistic: the settings page is a grid of checkboxes, and waiting for a
    // round trip per tick makes them feel broken.
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: LOCK_KEY });
      const prev = qc.getQueryData(LOCK_KEY);
      qc.setQueryData(LOCK_KEY, (p: any) => ({ ...p, leadViewDisplay: next }));
      return { prev };
    },
    onError: (e: any, _next, ctx: any) => {
      if (ctx?.prev !== undefined) qc.setQueryData(LOCK_KEY, ctx.prev);
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        leadViewDisplay: saved,
      }));
    },
  });
}

/**
 * Tenant-wide Add Lead field ORDER setter (Super Admin; the server rejects
 * anyone else). Optimistic: the customizer closes on Save, and the open form
 * behind it must re-render in the new order immediately rather than after the
 * round trip. Reads/writes the SAME cached config GET as the other org flags.
 */
export function useSetAddLeadFieldOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (addLeadFieldOrder: string[]): Promise<string[]> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/add-lead-order`,
        { addLeadFieldOrder },
      );
      return (res.data?.addLeadFieldOrder ?? []) as string[];
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: LOCK_KEY });
      const prev = qc.getQueryData(LOCK_KEY);
      qc.setQueryData(LOCK_KEY, (p: any) => ({ ...p, addLeadFieldOrder: next }));
      return { prev };
    },
    onError: (e: any, _next, ctx: any) => {
      if (ctx?.prev !== undefined) qc.setQueryData(LOCK_KEY, ctx.prev);
      toast.error(e?.response?.data?.message ?? "Failed to save the field order");
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        addLeadFieldOrder: saved,
      }));
    },
  });
}

/** Tenant-wide Add Lead HIDDEN field keys (Customize Add Lead → Hide). */
export function useAddLeadHiddenFields(): string[] {
  return useLeadFormLockedFields().addLeadHiddenFields;
}

/**
 * Tenant-wide Add Lead HIDDEN fields setter (Super Admin; the server rejects
 * anyone else, and never stores Name / Mobile). Optimistic for the same reason
 * as the order: the form behind the customizer must drop the field on Save.
 */
export function useSetAddLeadHiddenFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (addLeadHiddenFields: string[]): Promise<string[]> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/add-lead-hidden`,
        { addLeadHiddenFields },
      );
      return (res.data?.addLeadHiddenFields ?? []) as string[];
    },
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: LOCK_KEY });
      const prev = qc.getQueryData(LOCK_KEY);
      qc.setQueryData(LOCK_KEY, (p: any) => ({
        ...p,
        addLeadHiddenFields: next,
      }));
      return { prev };
    },
    onError: (e: any, _next, ctx: any) => {
      if (ctx?.prev !== undefined) qc.setQueryData(LOCK_KEY, ctx.prev);
      toast.error(
        e?.response?.data?.message ?? "Failed to save the hidden fields",
      );
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        addLeadHiddenFields: saved,
      }));
    },
  });
}

/**
 * Org-wide "the lead drawer shows a Move To Next Stage button" provision.
 * Reads off the same cached config GET as the other org flags (no extra
 * request).
 *
 * Defaults to `true` while the query is loading and on any read failure — the
 * button is the historical behaviour, so a config blip may never strip the
 * drawer's primary action. This is a UI provision, NOT a permission: turning it
 * off removes the one-click shortcut (which auto-picks the next stage's first
 * status); moving a lead through the stage/status picker is unaffected, and
 * `stage_management.move` still governs who may move one at all.
 */
export function useShowMoveToNextStage(): boolean {
  const { showMoveToNextStage } = useLeadFormLockedFields();
  return showMoveToNextStage;
}

export function useSetShowMoveToNextStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (showMoveToNextStage: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/move-to-next-stage`,
        { showMoveToNextStage },
      );
      return res.data?.showMoveToNextStage !== false;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        showMoveToNextStage: saved,
      }));
      toast.success(
        saved
          ? "Move To Next Stage is back on the lead view"
          : "Move To Next Stage is hidden for everyone",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Org-wide "follow-ups carry a time of day" flag.
 *
 * OFF means this tenant works follow-ups by DAY: the picker drops its time
 * step, no surface prints a time, and the server stores each new follow-up at
 * 00:00 IST of the chosen day.
 *
 * It changes NO follow-up rule. Overdue / Today / Later were always anchored on
 * the IST calendar day server-side, so "4 PM today" only turns Overdue at the
 * next midnight with times on OR off — this flag only decides whether a
 * time-of-day is captured and shown.
 *
 * Defaults to `true` while the config query loads and on any read failure: a
 * blip must degrade to "show the time we have", never to silently hiding a time
 * the tenant relies on.
 */
export function useFollowUpTimeEnabled(): boolean {
  const { followUpTimeEnabled } = useLeadFormLockedFields();
  return followUpTimeEnabled;
}

export function useSetFollowUpRemindMinutesBefore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (followUpRemindMinutesBefore: number): Promise<number> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/followup-remind-before`,
        { followUpRemindMinutesBefore },
      );
      return Number(res.data?.followUpRemindMinutesBefore) || 0;
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        followUpRemindMinutesBefore: saved,
      }));
      toast.success(
        saved
          ? `Follow-up reminders now pop up ${saved} min before`
          : "Follow-up reminders now pop up at the follow-up time",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

export function useSetFollowUpTimeEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (followUpTimeEnabled: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/followup-time`,
        { followUpTimeEnabled },
      );
      return res.data?.followUpTimeEnabled !== false;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        followUpTimeEnabled: saved,
      }));
      // Follow-up rows are cached by other queries (drawer, dashboard rail,
      // lead cards) and every one of them renders the time differently now.
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["v2-followups"] });
      toast.success(
        saved
          ? "Follow-ups now carry a time"
          : "Follow-ups are now date-only for everyone",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

export function useLeadBasicDetailsExpanded(): boolean {
  const { leadBasicDetailsExpanded } = useLeadFormLockedFields();
  return leadBasicDetailsExpanded;
}

export function useSetLeadBasicDetailsExpanded() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadBasicDetailsExpanded: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/basic-details-expanded`,
        { leadBasicDetailsExpanded },
      );
      return Boolean(res.data?.leadBasicDetailsExpanded);
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive — see
      // the note in useSetLeadFormLockedFields.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        leadBasicDetailsExpanded: saved,
      }));
      toast.success(
        saved
          ? "Basic details will open expanded for everyone"
          : "Basic details will open collapsed for everyone",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Org-wide "show the lead's ID in the lead view" flag. Reads off the same
 * cached config GET as the other org flags (no extra request). Defaults to
 * `false` while loading and on any read failure — hidden is the default state,
 * so a blip can only ever delay the ID, never flash it for a tenant that
 * turned it off.
 */
export function useShowLeadId(): boolean {
  const { showLeadId } = useLeadFormLockedFields();
  return showLeadId;
}

/**
 * Settings → Manage → "Pop assigned users". Each half is sent on its own, and
 * the server leaves the omitted half alone, so flipping one toggle can never
 * clobber the other one's value from a stale cache.
 *
 * Mentions are NOT covered here: being @-mentioned always pops.
 */
export function useSetChatAttachmentPopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: {
      leadUsers?: boolean;
      dealUsers?: boolean;
    }): Promise<{ leadUsers: boolean; dealUsers: boolean }> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/chat-attachment-popup`,
        {
          ...(patch.leadUsers !== undefined
            ? { chatAttachmentPopupLeadUsers: patch.leadUsers }
            : {}),
          ...(patch.dealUsers !== undefined
            ? { chatAttachmentPopupDealUsers: patch.dealUsers }
            : {}),
        },
      );
      return {
        leadUsers: res.data?.chatAttachmentPopupLeadUsers !== false,
        dealUsers: res.data?.chatAttachmentPopupDealUsers !== false,
      };
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        chatAttachmentPopupLeadUsers: saved.leadUsers,
        chatAttachmentPopupDealUsers: saved.dealUsers,
      }));
      toast.success(
        saved.leadUsers || saved.dealUsers
          ? "Attachment popups updated"
          : "Attachment popups are off — assignees will still get the toast",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Settings → Manage → "Pop assigned users" → Notes. Own endpoint, same
 * Super-Admin gate as the two attachment halves; the shared GET is patched in
 * place so the sibling flags survive.
 */
export function useSetNotePopup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(`${leadFormConfigEndpoint}/note-popup`, {
        notePopupAssignedUsers: enabled,
      });
      return res.data?.notePopupAssignedUsers !== false;
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        notePopupAssignedUsers: saved,
      }));
      toast.success(
        saved
          ? "Note popups are on"
          : "Note popups are off — assignees will still get the inbox row",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Settings → Manage → "Chat & notes sync": every note is posted into the
 * internal chat and every chat message is saved as a note (lead ↔ lead,
 * deal ↔ deal). The server does the copying; the client only uses this to
 * refresh the other side after its own writes. Defaults ON.
 */
export function useChatNotesSync(): boolean {
  const { chatNotesSync } = useLeadFormLockedFields();
  return chatNotesSync;
}

/** Super-Admin write for "Chat & notes sync" (own endpoint, shared GET patched). */
export function useSetChatNotesSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (chatNotesSync: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/chat-notes-sync`,
        { chatNotesSync },
      );
      return res.data?.chatNotesSync !== false;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        chatNotesSync: saved,
      }));
      toast.success(
        saved
          ? "Chat and notes are synced"
          : "Chat and notes sync is off — new notes and messages stay where they are written",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Settings → Manage → "Pop assigned users" → which roles get the popups.
 * Sends the WHOLE set (`[]` = every role); the server keeps only this
 * tenant's roles and answers with what it stored.
 */
export function useSetPopupRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roleIds: number[]): Promise<number[]> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/popup-roles`,
        { popupRoleIds: roleIds },
      );
      return parseRoleIds(res.data?.popupRoleIds);
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        popupRoleIds: saved,
      }));
      toast.success(
        saved.length === 0
          ? "Popups go to every role"
          : "Popup roles updated — other roles still get the toast and inbox row",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

export function useSetShowLeadId() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (showLeadId: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/show-lead-id`,
        { showLeadId },
      );
      return Boolean(res.data?.showLeadId);
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        showLeadId: saved,
      }));
      toast.success(
        saved
          ? "Lead IDs are now visible on the lead view"
          : "Lead IDs are hidden again",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * DEFAULT holder-role for the dashboard Follow-ups widgets — PER VIEWER.
 *
 * `resolved` is server-computed from the SESSION, not from tenant config
 * (2026-08-31 rule): a viewer with a role gets their OWN roleId — anybody
 * logged in opens on their own book — and tenant admins (no role) get `null`
 * = every holder, the merged team view. There is no tenant knob: the old
 * Settings → Manage "Follow-up Default Role" control and its Counsellor-like
 * automatic resolution were removed, and the server ignores whatever
 * historical value that column still stores. `stored` is now always null and
 * kept only for payload-shape stability.
 *
 * Reads off the same cached config GET as the other org settings — no extra
 * request on the dashboard.
 */
export function useDefaultFollowUpRole(): {
  stored: number | null;
  resolved: number | null;
  isLoading: boolean;
} {
  const { defaultFollowUpRoleId, resolvedDefaultFollowUpRoleId, isLoading } =
    useLeadFormLockedFields();
  return {
    stored: defaultFollowUpRoleId,
    resolved: resolvedDefaultFollowUpRoleId,
    isLoading,
  };
}

/**
 * Org-wide "leads may be looked up across the whole tenant" switch.
 *
 * Reads off the same cached config GET as the other org flags. Defaults to
 * `false` while loading and on any read failure — the entry points stay hidden
 * until the server has actually said the tenant opted in (the server re-checks
 * the flag on every lookup regardless, so a stale `true` here still 403s).
 */
export function useGlobalLeadSearchEnabled(): boolean {
  const { globalLeadSearchEnabled } = useLeadFormLockedFields();
  return globalLeadSearchEnabled;
}

/**
 * Org-wide "the stage picker offers one step forward only" provision.
 *
 * A PROCESS provision, not a permission: it narrows what the picker lists
 * (current stage + the one next stage, plus backward stages the role may roll
 * back to) for everyone including super admins, and never widens anything RBAC
 * denied. Defaults to `false` while loading and on any read failure — the
 * unrestricted picker is the historical behaviour.
 */
export function useShowOnlyNextStage(): boolean {
  const { showOnlyNextStage } = useLeadFormLockedFields();
  return showOnlyNextStage;
}

export function useSetShowOnlyNextStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (showOnlyNextStage: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/next-stage-only`,
        { showOnlyNextStage },
      );
      return Boolean(res.data?.showOnlyNextStage);
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        showOnlyNextStage: saved,
      }));
      toast.success(
        saved
          ? "Leads can now move one stage at a time"
          : "Every permitted stage is selectable again",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Org-wide "Add/Edit Created At" provision. When on, the Add Lead form offers
 * a Created At date (defaults to today) and the lead view's Basic Details
 * edit mode lets the editor change it. Off by default; the server rejects a
 * `createdAt` on create/PATCH while it's off, so this only decides whether
 * the field is RENDERED — never whether the write is accepted.
 */
export function useAllowCreatedAtEdit(): boolean {
  const { allowCreatedAtEdit } = useLeadFormLockedFields();
  return allowCreatedAtEdit;
}

export function useSetAllowCreatedAtEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (allowCreatedAtEdit: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/created-at-edit`,
        { allowCreatedAtEdit },
      );
      return res.data?.allowCreatedAtEdit === true;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        allowCreatedAtEdit: saved,
      }));
      toast.success(
        saved
          ? "Created At can now be set when adding or editing a lead"
          : "Created At is system-set again",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Org-wide "Disable board view" provision. When on, the Board tab is gone from
 * the leads page, stage routes, favorite views and shared views, and a saved
 * "Board" pick falls back to Grid (see `useLeadViewTabs`). Off by default.
 * UI-only: the board endpoint is untouched — it serves the same leads the
 * list does, so there is nothing for the server to guard.
 *
 * ADMINS ARE NEVER HIDDEN FROM (user directive 2026-09-29): super admins,
 * platform/system admins and ADMIN-preset roles keep the Board tab however the
 * toggle is set — `selectIsBoardViewHideExempt`. The provision steers the
 * team's day-to-day views; the people who own the pipeline still need to look
 * at it stage by stage. Baked into this one hook, not into each call site, so
 * the tab list, the saved-pick bounce and the kanban render can never disagree
 * about who gets a board.
 *
 * Settings → Manage reads the STORED flag through `useLeadFormLockedFields`
 * instead, so the toggle always shows what is saved rather than what the
 * admin looking at it sees.
 */
export function useBoardViewDisabled(): boolean {
  const { boardViewDisabled } = useLeadFormLockedFields();
  const isExempt = useSelector(selectIsBoardViewHideExempt);
  return boardViewDisabled && !isExempt;
}

/**
 * "Disable deals" (Settings → Manage → Deals). ON = the workspace has no deal
 * concept: no Deals header, no deal tabs, no attach slot, no deal filters or
 * metric, and the server refuses to create one. Default OFF.
 */
export function useDealsDisabled(): boolean {
  const { dealsDisabled } = useLeadFormLockedFields();
  return dealsDisabled;
}

export function useSetDealsDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dealsDisabled: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/deals-disabled`,
        { dealsDisabled },
      );
      return res.data?.dealsDisabled === true;
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        dealsDisabled: saved,
      }));
      // Every deal surface reads this flag; the lead lists and drawer refetch
      // so a hidden deal never lingers on screen.
      qc.invalidateQueries({ queryKey: ["leads"] });
      toast.success(
        saved
          ? "Deals are now off for this workspace"
          : "Deals are available again",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

export function useSetBoardViewDisabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (boardViewDisabled: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/board-view`,
        { boardViewDisabled },
      );
      return res.data?.boardViewDisabled === true;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        boardViewDisabled: saved,
      }));
      toast.success(
        saved
          ? "Board view is now hidden for everyone except admins"
          : "Board view is available again",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Settings → Manage → "Lead View Tabs" — which lead-drawer tabs the whole
 * tenant sees. Sends the WHOLE hidden set (`[]` = every tab shown); the server
 * keeps only tab names it knows and answers with what it stored.
 */
export function useSetHiddenLeadViewTabs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (hiddenLeadViewTabs: string[]): Promise<string[]> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/lead-view-tabs`,
        { hiddenLeadViewTabs },
      );
      return parseTabNames(res.data?.hiddenLeadViewTabs);
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        hiddenLeadViewTabs: saved,
      }));
      toast.success("Lead view tabs updated for everyone");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Org-wide "Hide branch filters". When on, the dashboard's Branch dropdowns
 * (top filter bar, Follow-ups widget, Recent activity page) are gone and any
 * branch pick left over is reset to "All branches" (`useV2FilterOptions`).
 * Off by default. UI-only: every dashboard endpoint still applies the viewer's
 * own branch scope, so hiding a narrowing control widens nobody's reach.
 */
export function useDashboardBranchFilterHidden(): boolean {
  const { dashboardBranchFilterHidden } = useLeadFormLockedFields();
  return dashboardBranchFilterHidden;
}

export function useSetDashboardBranchFilterHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dashboardBranchFilterHidden: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/dashboard-branch-filter`,
        { dashboardBranchFilterHidden },
      );
      return res.data?.dashboardBranchFilterHidden === true;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        dashboardBranchFilterHidden: saved,
      }));
      toast.success(
        saved
          ? "Branch filters are now hidden on the dashboard"
          : "Branch filters are back on the dashboard",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * "Globalise configs" (Settings → Manage). When ON, the layouts a tenant
 * admin saves — card Show/hide fields, list column order and widths, the
 * lead-drawer tab strip — are the layouts for everyone: the boot merge puts
 * the workspace layout OVER a user's own row, and non-admins get those
 * controls read-only (see `useLayoutLocked`). Off by default.
 */
export function useViewConfigGlobalised(): boolean {
  const { viewConfigGlobalised } = useLeadFormLockedFields();
  return viewConfigGlobalised;
}

export function useSetViewConfigGlobalised() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (viewConfigGlobalised: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/globalise-configs`,
        { viewConfigGlobalised },
      );
      return res.data?.viewConfigGlobalised === true;
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        viewConfigGlobalised: saved,
      }));
      toast.success(
        saved
          ? "Layouts are now global — everyone sees the admin's arrangement"
          : "Layouts are personal again",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Settings → Manage "Dashboard default role": which role the dashboard's
 * FOLLOW-UP Role pills (rail, Follow-ups by user, Follow-ups by branch) open
 * on. "auto" = the first personal role under the viewer (today's rule);
 * "current_role" = the logged-in user's own role. The resolved id itself
 * ships from /core/dashboard/filter-options (`followUpDefaultRoleId`), so the
 * save also refreshes that query.
 */
export function useSetFollowUpDefaultRoleMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      followUpDefaultRoleMode: FollowUpDefaultRoleMode,
    ): Promise<FollowUpDefaultRoleMode> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/followup-default-role`,
        { followUpDefaultRoleMode },
      );
      return res.data?.followUpDefaultRoleMode === "current_role"
        ? "current_role"
        : "auto";
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        followUpDefaultRoleMode: saved,
      }));
      // The seed rides the dashboard filter-options payload — refetch it so
      // the pills re-open on the new rule without a reload.
      qc.invalidateQueries({ queryKey: ["v2-filter-options"] });
      toast.success(
        saved === "current_role"
          ? "Follow-up filters now open on each person's own role"
          : "Follow-up filters are back to automatic",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * The tenant's "Default view" — which side of the leads list's Active /
 * Inactive toggle a list should OPEN on.
 *
 * Returns `null` while the config query is still in flight, so callers can
 * skip seeding rather than stamping "all" over the real answer a tick later.
 * A FAILED read resolves to "all": the default may widen a list, never hide
 * leads from someone who is looking for them.
 */
export function useDefaultLeadView(): DefaultLeadView | null {
  const { defaultLeadView, isLoading } = useLeadFormLockedFields();
  return isLoading ? null : defaultLeadView;
}

export function useSetDefaultLeadView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      defaultLeadView: DefaultLeadView,
    ): Promise<DefaultLeadView> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/default-lead-view`,
        { defaultLeadView },
      );
      return res.data?.defaultLeadView === "Active"
        ? "Active"
        : res.data?.defaultLeadView === "Inactive"
          ? "Inactive"
          : "all";
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        defaultLeadView: saved,
      }));
      toast.success(
        saved === "all"
          ? "Lead lists now open on All"
          : `Lead lists now open on ${saved}`,
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

export function useSetSidebarDefaultOpen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      sidebarDefaultOpen: SidebarDefaultOpen,
    ): Promise<SidebarDefaultOpen> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/sidebar-default-open`,
        { sidebarDefaultOpen },
      );
      return res.data?.sidebarDefaultOpen === "stages" ? "stages" : "favorites";
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        sidebarDefaultOpen: saved,
      }));
      toast.success(
        saved === "stages"
          ? "The sidebar now opens on Stages"
          : "The sidebar now opens on Favorite Views",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

export function useSetGlobalLeadSearchEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (globalLeadSearchEnabled: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/global-lead-search`,
        { globalLeadSearchEnabled },
      );
      return Boolean(res.data?.globalLeadSearchEnabled);
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        globalLeadSearchEnabled: saved,
      }));
      toast.success(
        saved
          ? "Global lead search is on"
          : "Global lead search is off",
      );
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Org-wide "show previous (revoked) assignees to admins" flag. Reads off the
 * same cached config query as the locked-fields hook (no extra request), so the
 * lead drawer can cheaply decide whether to surface previous assignees.
 */
export function useShowPreviousAssignees(): boolean {
  const { showPreviousAssignees } = useLeadFormLockedFields();
  return showPreviousAssignees;
}

export function useSetShowPreviousAssignees() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (showPreviousAssignees: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/show-previous-assignees`,
        { showPreviousAssignees },
      );
      return Boolean(res.data?.showPreviousAssignees);
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        showPreviousAssignees: saved,
      }));
      toast.success(
        saved ? "Previous assignees are now visible to admins" : "Previous assignees hidden",
      );
    },
    onError: (e: any) => {
      toast.error(
        e?.response?.data?.message ?? "Failed to save this setting",
      );
    },
  });
}

export function useSetLeadFormLockedFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (lockedFields: string[]): Promise<string[]> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/locked-fields`,
        { lockedFields },
      );
      return (res.data?.lockedFields ?? []) as string[];
    },
    onSuccess: (saved) => {
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        // Preserve the sibling flags — this GET is shared, and dropping them
        // here would silently switch the admin "previous assignees" view (or
        // phone masking) off until the next refetch.
        ...prev,
        lockableFields: prev?.lockableFields ?? [],
        lockedFields: saved,
        showPreviousAssignees: prev?.showPreviousAssignees ?? false,
        maskLeadPhone: prev?.maskLeadPhone ?? false,
      }));
      toast.success("Field permissions saved");
    },
    onError: (e: any) => {
      toast.error(
        e?.response?.data?.message ?? "Failed to save field permissions",
      );
    },
  });
}

export function useSetLeadFormRequiredFields() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requiredFields: string[]): Promise<string[]> => {
      const res = await axiosInstance.put(leadFormConfigEndpoint, { requiredFields });
      return (res.data?.requiredFields ?? []) as string[];
    },
    onSuccess: (saved) => {
      qc.setQueryData(KEY, saved);
      toast.success("Required fields saved");
    },
    onError: (e: any) => {
      toast.error(
        e?.response?.data?.message ?? "Failed to save required fields",
      );
    },
  });
}

/**
 * Tenant-wide landing SUB-STATUS for "Give it as a fresh lead" (null = the
 * first sub-status of the first stage's first status). Reads off the shared
 * config GET. The server applies it on every fresh hand-over — assign, bulk
 * assign, branch transfer — for every user; the client never sends it with
 * the assign call.
 */
export function useFreshLeadSubStatusId(): number | null {
  const { freshLeadSubStatusId } = useLeadFormLockedFields();
  return freshLeadSubStatusId;
}

export function useSetFreshLeadSubStatusId() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      freshLeadSubStatusId: number | null,
    ): Promise<number | null> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/fresh-lead-sub-status`,
        { freshLeadSubStatusId },
      );
      return Number(res.data?.freshLeadSubStatusId) > 0
        ? Number(res.data.freshLeadSubStatusId)
        : null;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        freshLeadSubStatusId: saved,
      }));
      toast.success("Fresh leads will now land on this sub-status for everyone");
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Settings → Manage "Check-in / Check-out". PARTIAL body: send only the key
 * that changed; the server leaves the others alone and returns the whole
 * settings object, which replaces `checkInOut` on the shared GET.
 */
export function useSetCheckInOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      patch: Partial<CheckInOutSettings>,
    ): Promise<CheckInOutSettings> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/check-in-out`,
        patch,
      );
      return parseCheckInOut(res.data?.checkInOut);
    },
    onSuccess: (saved, patch) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        lockedFields: prev?.lockedFields ?? [],
        lockableFields: prev?.lockableFields ?? [],
        checkInOut: saved,
      }));
      if (patch.enabled !== undefined) {
        toast.success(
          saved.enabled
            ? "Check-in / check-out is on for your personal-scope roles"
            : "Check-in / check-out is off — everyone has been checked out",
        );
      } else {
        toast.success("Check-in settings saved");
      }
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? "Failed to save this setting");
    },
  });
}

/**
 * Settings → Manage "Count only CRM numbers" (2026-10-07). OFF (the default):
 * every call the team made or took counts in the call numbers — dashboard
 * card, KPI strip, Reports' Call Analytics tiles and rankboard, mobile, Xale
 * AI. ON: only calls filed under a lead count; the rest is shown apart as
 * Unknown. Enforced on the SERVER (the call endpoints change what they
 * return), so this is only the stored flag for the settings card and the
 * captions that say which calls a number counts.
 */
export function useCallAnalyticsCrmOnly(): boolean {
  const { callAnalyticsCrmOnly } = useLeadFormLockedFields();
  return callAnalyticsCrmOnly;
}

/** Query keys whose payloads change with the mode — dropped after a save so
 *  every open call surface refetches in the new mode (the server already
 *  swept its own cache). */
const CALL_SCOPE_QUERY_KEY = /call-analytics|call-leaderboard|kpi-summary/;

/**
 * "WhatsApp number counts as the lead's number" (Settings → Manage, 2026-10-07).
 * Super Admin only (server-gated). Shares the locked-fields GET.
 */
export function useSetWhatsappNumberUnique() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (whatsappNumberUnique: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/whatsapp-number-unique`,
        { whatsappNumberUnique },
      );
      return res.data?.whatsappNumberUnique === true;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(
        LOCK_KEY,
        (prev: Record<string, unknown> | undefined) => ({
          ...prev,
          lockedFields: prev?.lockedFields ?? [],
          lockableFields: prev?.lockableFields ?? [],
          whatsappNumberUnique: saved,
        }),
      );
      toast.success(
        saved
          ? "A lead's WhatsApp number now counts in the duplicate check"
          : "The duplicate check now looks at the phone number only",
      );
    },
    onError: (e: unknown) => {
      const message = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message || "Could not save the duplicate-number rule");
    },
  });
}

/**
 * "Denied stages stay hidden everywhere" (Settings → Manage, 2026-10-26).
 * Super Admin only (server-gated). Shares the locked-fields GET.
 */
export function useSetStageDenyStrict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (stageDenyStrict: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/stage-deny-strict`,
        { stageDenyStrict },
      );
      return res.data?.stageDenyStrict !== false;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(
        LOCK_KEY,
        (prev: Record<string, unknown> | undefined) => ({
          ...prev,
          lockedFields: prev?.lockedFields ?? [],
          lockableFields: prev?.lockableFields ?? [],
          stageDenyStrict: saved,
        }),
      );
      toast.success(
        saved
          ? "Leads in a denied stage are now hidden everywhere"
          : "Denied stages now follow the previous visibility rules",
      );
    },
    onError: (e: unknown) => {
      const message = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message || "Could not save the denied-stage setting");
    },
  });
}

/**
 * The industry whose payment analytics start OFF — only the FALLBACK below
 * uses it (the server owns the real default and ships the resolved value).
 */
const PAYMENT_ANALYTICS_OFF_BY_DEFAULT_INDUSTRY_ID = 1;

/**
 * "Payment analytics" (Settings → Manage, 2026-10-31) — THE gate for every
 * money surface on the web app: the dashboard Payments / Payments by person
 * cards and revenue tile, Branch Performance's Revenue metric, Reports →
 * Revenue, the Revenue report cards, the pipeline-value figures on the report
 * cards and money target metrics.
 *
 * The server sends the RESOLVED value on GET /form-config (a stored choice,
 * else the industry default: off for study abroad, on elsewhere). Until it
 * answers — and on an older server that never sends it — this falls back to
 * exactly that old rule, so nothing appears or disappears before the server
 * says so.
 */
export function usePaymentAnalyticsEnabled(): boolean {
  const { paymentAnalytics } = useLeadFormLockedFields();
  const industryId = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.auth?.currentUser?.user?.tenant?.industryId,
  );
  return (
    paymentAnalytics ??
    Number(industryId) !== PAYMENT_ANALYTICS_OFF_BY_DEFAULT_INDUSTRY_ID
  );
}

/** "Conversion goal" for one pipeline — Super Admin only (server-gated). A null
 *  `stageId` puts that pipeline back on its last stage. */
export function useSetConversionGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      goal: Omit<ConversionGoal, "stageId"> & { stageId: number | null },
    ): Promise<ConversionGoal[]> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/conversion-goal`,
        goal,
      );
      return Array.isArray(res.data?.conversionGoals) ? res.data.conversionGoals : [];
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(
        LOCK_KEY,
        (prev: Record<string, unknown> | undefined) => ({
          ...prev,
          lockedFields: prev?.lockedFields ?? [],
          lockableFields: prev?.lockableFields ?? [],
          conversionGoals: saved,
        }),
      );
      // Every Conversions figure changes meaning — let them all refetch.
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== LOCK_KEY[0] });
      toast.success("Conversion goal updated");
    },
    onError: (e: unknown) => {
      const message = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message ?? "Could not update the conversion goal");
    },
  });
}

/** "Payment analytics" — Super Admin only (server-gated). Shares the locked-fields GET. */
export function useSetPaymentAnalytics() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (paymentAnalytics: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/payment-analytics`,
        { paymentAnalytics },
      );
      return res.data?.paymentAnalytics === true;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(
        LOCK_KEY,
        (prev: Record<string, unknown> | undefined) => ({
          ...prev,
          lockedFields: prev?.lockedFields ?? [],
          lockableFields: prev?.lockableFields ?? [],
          paymentAnalytics: saved,
        }),
      );
      // Every money card, tile and report reads the switch — let them all
      // refetch rather than keep a payload from before the flip.
      qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== LOCK_KEY[0] });
      toast.success(
        saved
          ? "Payment analytics are now shown"
          : "Payment analytics are now hidden",
      );
    },
    onError: (e: unknown) => {
      const message = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message || "Could not save the payment analytics setting");
    },
  });
}

export function useSetCallAnalyticsCrmOnly() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (callAnalyticsCrmOnly: boolean): Promise<boolean> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/call-analytics-scope`,
        { callAnalyticsCrmOnly },
      );
      return res.data?.callAnalyticsCrmOnly === true;
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(
        LOCK_KEY,
        (prev: Record<string, unknown> | undefined) => ({
          ...prev,
          lockedFields: prev?.lockedFields ?? [],
          lockableFields: prev?.lockableFields ?? [],
          callAnalyticsCrmOnly: saved,
        }),
      );
      void qc.invalidateQueries({
        predicate: (q) =>
          typeof q.queryKey[0] === "string" &&
          CALL_SCOPE_QUERY_KEY.test(q.queryKey[0]),
      });
      toast.success(
        saved
          ? "Call analytics now count only CRM numbers"
          : "Call analytics now count every call",
      );
    },
    onError: (e: unknown) => {
      const message = (e as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      toast.error(message ?? "Failed to save this setting");
    },
  });
}

/**
 * Tenant-wide ORDER of the fields in ONE lead-drawer section (basic / leadPref
 * / dealPref / source) — a Super Admin drags the inputs in the drawer's edit
 * mode (`SortableFieldGrid`) and everyone reads the result off
 * `leadViewDisplay.fieldOrder.<section>`. Optimistic on the shared config
 * cache so the grid settles in its new order the moment the drop lands; the
 * server answers with the whole display blob, which replaces the cache entry.
 */
export function useSetLeadViewFieldOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      section,
      order,
    }: {
      section: string;
      order: string[];
    }): Promise<Record<string, any>> => {
      const res = await axiosInstance.put(
        `${leadFormConfigEndpoint}/view-field-order`,
        { section, order },
      );
      return (res.data?.leadViewDisplay ?? {}) as Record<string, any>;
    },
    onMutate: async ({ section, order }) => {
      await qc.cancelQueries({ queryKey: LOCK_KEY });
      const prev = qc.getQueryData(LOCK_KEY);
      qc.setQueryData(LOCK_KEY, (p: any) => ({
        ...p,
        leadViewDisplay: {
          ...(p?.leadViewDisplay ?? {}),
          fieldOrder: {
            ...(p?.leadViewDisplay?.fieldOrder ?? {}),
            [section]: order,
          },
        },
      }));
      return { prev };
    },
    onError: (e: any, _next, ctx: any) => {
      if (ctx?.prev !== undefined) qc.setQueryData(LOCK_KEY, ctx.prev);
      toast.error(e?.response?.data?.message ?? "Failed to save the field order");
    },
    onSuccess: (saved) => {
      // `...prev` FIRST so the sibling flags on this shared GET survive.
      qc.setQueryData(LOCK_KEY, (prev: any) => ({
        ...prev,
        leadViewDisplay: saved,
      }));
      toast.success("Field order saved for your workspace");
    },
  });
}
